/**
 * RevealText: renders copy hidden behind a "redacted" block that wipes away
 * to expose the text, giving the research narrative a declassified-document
 * feel. Reveals once when scrolled into view and respects reduced-motion
 * preferences (and the app-wide no-animations performance mode, which zeroes
 * CSS transitions the same way it zeroes animations).
 */

import React, { useEffect, useRef, useState } from "react";

interface RevealTextProps {
  children: React.ReactNode;
  className?: string;
  /** Milliseconds to wait before the wipe starts (use to stagger a list). */
  delay?: number;
  /** Milliseconds the wipe takes. */
  duration?: number;
}

export default function RevealText({
  children,
  className = "",
  delay = 0,
  duration = 650,
}: RevealTextProps): React.ReactElement {
  const ref = useRef<HTMLSpanElement>(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    if (
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      setRevealed(true);
      return;
    }

    let timer: number | undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          timer = window.setTimeout(() => setRevealed(true), delay);
          observer.disconnect();
        }
      },
      { threshold: 0.15 },
    );
    observer.observe(node);
    return () => {
      observer.disconnect();
      if (timer) window.clearTimeout(timer);
    };
  }, [delay]);

  return (
    <span ref={ref} className={`relative inline-block ${className}`}>
      <span
        className="relative"
        style={{
          opacity: revealed ? 1 : 0,
          transition: "opacity 380ms ease-out",
          // Text fades in only once the block is roughly half wiped away.
          transitionDelay: revealed ? `${duration * 0.45}ms` : "0ms",
        }}
      >
        {children}
      </span>
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 overflow-hidden rounded-[3px]"
      >
        <span
          className="absolute inset-0 origin-left bg-gradient-to-r from-ink-600 via-ink-500 to-ink-600"
          style={{
            transform: revealed ? "scaleX(0)" : "scaleX(1)",
            transition: `transform ${duration}ms cubic-bezier(0.22, 1, 0.36, 1)`,
          }}
        />
      </span>
    </span>
  );
}
